/**
 * Connects the screens to the backend: one REST client, one SignalR connection, shared through context.
 * Not run against your app. Needs `npm i @microsoft/signalr`.
 *
 *   <CrmBackendProvider token={authToken}> ... </CrmBackendProvider>
 *
 * Children use `useCrmBackend()`:
 *   api / crm       REST clients (crmApi.ts raw, crmClient.ts mapped for the six CRM.tsx entities)
 *   state           "connecting" | "connected" | "reconnecting" | "disconnected" | "off" (no token)
 *   subscribe(fn)   fn(change) runs for every live "changed" message except the ones this person made themselves
 *   resyncTick      a number that goes up after a lost connection came back. Reload what is on screen when it changes.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createCrmApi, type CrmApi } from "./Api/Crmapi";
import { createCrmClient, fetchTransport, type CrmClient } from "./Api/crmClient";
import { connectCrmRealtime, type CrmChange, type RealtimeState } from "../../types/Crmrealtime";
import { CRM_BASE_URL, CRM_LANGUAGE, getCrmToken } from "../../types/Crmconfig";

export type BackendState = RealtimeState | "off";
export interface CrmBackend {
  api: CrmApi;
  crm: CrmClient;
  state: BackendState;
  subscribe: (fn: (c: CrmChange) => void) => () => void;
  resyncTick: number;
  /** the signed-in person's id (the token's `sub`), or undefined when it cannot be read */
  me?: string;
  /** set when the server answered 401: the token is missing, expired or not accepted */
  unauthorized: boolean;
}

const Ctx = createContext<CrmBackend | null>(null);

/** Reads `sub` from a JWT payload. Only used to skip our own echo; never for security. */
export function tokenSub(token?: string): string | undefined {
  try {
    if (!token) return undefined;
    const p = token.split(".")[1];
    const json = decodeURIComponent(atob(p.replace(/-/g, "+").replace(/_/g, "/")).split("").map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""));
    const o = JSON.parse(json);
    return o.sub ?? o.nameid ?? o["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
  } catch { return undefined; }
}

export const CrmBackendProvider: React.FC<{ token?: string | null; baseUrl?: string; children: React.ReactNode }> = ({ token, baseUrl = CRM_BASE_URL, children }) => {
  const tokenRef = useRef<string | undefined>(token ?? getCrmToken());
  tokenRef.current = token ?? getCrmToken();
  const [state, setState] = useState<BackendState>("off");
  const [resyncTick, setResync] = useState(0);
  const [unauthorized, setUnauthorized] = useState(false);
  const listeners = useRef(new Set<(c: CrmChange) => void>());
  const me = useMemo(() => tokenSub(tokenRef.current), [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = useCallback(() => tokenRef.current, []);
  const onUnauthorized = useCallback(() => setUnauthorized(true), []);
  const api = useMemo(() => createCrmApi({ baseUrl, getToken, language: CRM_LANGUAGE, onUnauthorized }), [baseUrl, getToken, onUnauthorized]);
  const crm = useMemo(() => createCrmClient({ transport: fetchTransport(baseUrl), getToken, language: CRM_LANGUAGE, onUnauthorized }), [baseUrl, getToken, onUnauthorized]);

  const subscribe = useCallback((fn: (c: CrmChange) => void) => { listeners.current.add(fn); return () => { listeners.current.delete(fn); }; }, []);

  // reopen the connection when the token changes: the server reads the person's permissions when it opens
  useEffect(() => {
    setUnauthorized(false);
    if (!tokenRef.current) { setState("off"); return; }
    const rt = connectCrmRealtime({
      baseUrl,
      getToken,
      onState: setState,
      onResync: () => setResync((n) => n + 1),
      onChange: (c) => {
        if (me && c.byId && c.byId === me) return; // our own change: the screen already has it
        listeners.current.forEach((fn) => { try { fn(c); } catch { /* one listener must not stop the others */ } });
      },
    });
    return () => { void rt.stop(); };
  }, [token, baseUrl, getToken, me]);

  const value = useMemo<CrmBackend>(() => ({ api, crm, state, subscribe, resyncTick, me, unauthorized }), [api, crm, state, subscribe, resyncTick, me, unauthorized]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/** Null when there is no provider, so CRM.tsx keeps working with its sample data in that case. */
export const useCrmBackend = (): CrmBackend | null => useContext(Ctx);